<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Invoice" avatarImage="/glass/img/accounts-overview-invoice.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>{{invoiceTitle}}</h4>

          <template v-if="hasIntegrations">
            <h5>Integrations</h5>
            <p>
              <LinkIntegrationLink
                v-for="integration in integrationList"
                :key="integration.code"
                :integration="integration"
                @refresh="reloadData()" />
            </p>
          </template>

          <OverviewBoxInfoItem v-if="invoice.contact" icon="fa-user-circle">
            <LinkContact :id="invoice.contactId" :name="invoice.contact.name" />
          </OverviewBoxInfoItem>

          <OverviewBoxInfoItem icon="fa-shopping-cart" v-if="invoice.orderId && invoice.order">
            <LinkOrder :id="invoice.orderId" :name="invoice.order.sequenceNumber || 'Order'" />                        
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-tag" v-if="invoice.customerRef">
            {{invoice.customerRef}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle">
            {{invoice.totalPrice | formatCurrency}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-calendar-exclamation">
            {{invoice.dateDue | formatDate}}
          </OverviewBoxInfoItem>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <LabelBadge :labelClass="`label-invoice-status-${invoice.status}`">{{invoice.status | formatLookupName(InvoiceStatusTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="invoice.sequenceNumber">{{invoice.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{invoice.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{invoice.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-invoice-summary">
              <i class="far fa-tasks"></i> Summary
            </Tab>
            <Tab ref="detailsTab" tabid="tab-invoice-details">
              <i class="far fa-clipboard-list-check"></i> Details
            </Tab>
            <Tab tabid="tab-invoice-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-invoice-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-invoice-summary">
              <div class="row">
                <div class="col-lg-12" v-if="invoice.id">

                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="editInvoice()" icon="fa-pencil" v-if="isInvoiceEditable">Edit Invoice</DropdownMenuItem>
                    <DropdownMenuItem @click="sendInvoice()" icon="fa-envelope" v-if="isInvoiceReSendable">Re-send Invoice</DropdownMenuItem>
                    <DropdownMenuItem @click="closeInvoice()" icon="fa-check-circle" v-if="isInvoiceClosable">Close Invoice</DropdownMenuItem>
                    <DropdownMenuDivider v-if="(isInvoiceReSendable || isInvoiceClosable) && (isAddPaymentAvailable || isAddCreditNoteAvailable)" />
                    <DropdownMenuItem @click="addPayment()" icon="fa-credit-card" v-if="isAddPaymentAvailable">Add Payment</DropdownMenuItem>
                    <DropdownMenuItem @click="addCreditNote()" icon="fa-money-check-edit" v-if="isAddCreditNoteAvailable">Add Credit Note</DropdownMenuItem>
                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="displayInvoiceReport()" icon="fa-print">Invoice Sheet</DropdownMenuItem>
                  </ActionDropMenu>

                  <ProgressTracker>
                    <ProgressTrackerItem title="Invoice Created" :completed="true" :cancelled="isInvoiceCancelled" icon="fa-list-alt" :timestamp="invoice.createdAt">
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-3">
                            <h5>CUSTOMER</h5>
                            {{invoice.contact.name}}
                            <template v-if="invoice.customContactName">
                              <h5 class="mt-2 mb-0">CUSTOM CONTACT PERSON</h5>
                              {{invoice.customContactName}}
                              <div v-if="invoice.customContactEmail">{{invoice.customContactEmail}}</div>
                              <div v-if="invoice.customContactMobile">{{invoice.customContactMobile}}</div>
                            </template>
                          </div>
                          <div class="col-lg-3">
                            <h5>AMOUNT</h5>
                            {{invoice.totalPrice | formatCurrency}}
                          </div>
                          <div class="col-lg-3">
                            <h5>AMOUNT DUE</h5>
                            {{invoice.amountDue | formatCurrency}}
                          </div>
                          <div class="col-lg-3">
                            <h5>DUE</h5>
                            {{invoice.dateDue | formatDate}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      title="Waiting Payment"
                      :completed="invoice.status == InvoiceStatusTypes.paid"
                      :cancelled="isInvoiceCancelled"
                      :current="isInvoiceWaitingPayment"
                      icon="fa-credit-card"
                      note="Waiting for customer to pay invoice"
                      :timestamp="invoice.dateClosed">
                      <template v-slot:all>
                        <InvoicePaymentList :invoicePaymentList="invoice.invoicePaymentAllocations" />
                      </template>
                      <template v-slot:current>
                        <div class="mt-4"> </div>
                        <ProgressTrackerButton @click="sendInvoice" icon="fa-paper-plane">Resend Invoice</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="addPayment" icon="fa-credit-card" v-if="isAddPaymentAvailable">Add Payment</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="addCreditNote" icon="fa-money-check-edit" v-if="isAddCreditNoteAvailable">Add Credit Note</ProgressTrackerButton>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!isInvoiceCancelled"
                      title="Completed"
                      :completed="invoice.status == InvoiceStatusTypes.paid"
                      :cancelled="isInvoiceCancelled"
                      icon="fa-check"
                      :timestamp="invoice.dateClosed"
                      note="Waiting for customer to pay invoice">
                      <template v-slot:completed>
                        <p>Invoice Paid</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isInvoiceCancelled"
                      title="Invoice Cancelled"
                      :cancelled="true"
                      :timestamp="invoice.dateClosed">
                      <template v-slot:cancelled>
                        <div class="row">
                          <div class="col-lg-12">
                            <h5>CANCELLED REASON</h5>
                            {{invoice.cancelledReason}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>


                  </ProgressTracker>

                </div>
              </div>
              
            </TabPage>

            <TabPage tabid="tab-invoice-details">
              <DataPageArea title="Details">
                <template slot="actionArea">
                  
                </template>
                <template slot="dataArea">
                  
                  <DataItem title="Customer" v-if="invoice.contact">{{invoice.contact.name}}</DataItem>
                  <DataItem title="Custom Contact Person" v-if="invoice.customContactName">
                    {{invoice.customContactName}} <span v-if="invoice.customContactEmail">-</span> {{invoice.customContactEmail}}
                    <div v-if="invoice.customContactMobile">{{invoice.customContactMobile}}</div>
                    <div v-if="invoice.customContactDetails">
                      <span v-html="convertNewlinesToBreaks(invoice.customContactDetails)" />
                    </div>
                  </DataItem>
                  <DataItem title="Customer Reference">{{invoice.customerRef}}</DataItem>
                  <DataItem title="Order" v-if="invoice.order">{{invoice.order.sequenceNumber}}</DataItem>
                  <DataItem title="Date Invoiced">{{invoice.dateInvoiceCreated | formatDateTime}}</DataItem>
                  <DataItem title="Due Date">{{invoice.dateDue | formatDate}}</DataItem>
                  <DataItem title="Due Completed" v-if="isInvoiceCompleted">{{invoice.dateClosed | formatDateTime}}</DataItem>
                  <DataItem title="Due Cancelled" v-if="isInvoiceCancelled">{{invoice.dateClosed | formatDateTime}}</DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-3">
                <div class="col-lg-12">
                  <h3>Items</h3>

                  <FormGroup :form="itemList.form">

                    <OrderItemList
                      ref="invoiceItemList"
                      :value="itemList.itemData"
                      :editing="false"
                      :contactId="invoice.contactId"
                      :showTotals="true"
                      :showGlassTotals="false"
                      :showMarkAs="false"
                      :showPriceGroups="false"
                      :optionCustomerApprovalAvailable="false"
                      :optionCustomFieldsAvailable="false"
                      :optionExternalAvailable="false"
                      :optionEdgeworkAvailable="false"

                      :optionAddServiceAvailable="false"
                      :optionAddGeneralAvailable="false"
                      :optionNewItemGlassAvailable="false"
                      :optionNewItemGeneralAvailable="false"
                      :optionNewItemTemplateAvailable="false"
                      :optionNewItemAssemblyAvailable="false"
                      :optionSupplierDetailsAvailable="false"
                      :optionCancelItemAvailable="false"
                      :optionBlockRemovingExternal="false"
                    />
                  </FormGroup>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-invoice-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.invoice"
                    :linkId="invoice.id"
                    :permissionResource="PermissionTypes.AccountsPermissionTypes.invoice"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-invoice-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.invoice" :linkId="invoice.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'

import ProgressTracker from '@/components/layout/progressTracker.vue'
import ProgressTrackerItem from '@/components/layout/progressTrackerItem.vue'
import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import DocumentList from '@/components/documentList.vue'

import OrderItemList from '@/modules/orders/components/orderItemList'
import InvoicePaymentList from '@/modules/accounts/components/invoicePaymentList'

import SendInvoiceDialog from '@/modules/accounts/views/sendInvoiceDialog'
import AddPaymentDialog from '@/modules/accounts/views/addPaymentDialog'
import AddCreditNoteDialog from '@/modules/accounts/views/addCreditNoteDialog'
import EditInvoiceDialog from '@/modules/accounts/views/editInvoiceDialog'
import CloseInvoiceDialog from '@/modules/accounts/views/closeInvoiceDialog'

import { InvoiceStatusTypes, InvoiceStatusTypeLookup, LinkTypes } from '@/store/types'


export default {
  name: 'Invoice',
  mixins: [form, pdfMixin, orderItemMixin],
  components: {
    ProgressTracker,
    ProgressTrackerItem,
    ProgressTrackerButton,
    DocumentList,
    OrderItemList,
    InvoicePaymentList
  },
  computed: {
    invoiceTitle() {
      var title = 'Invoice'
      if(this.invoice.sequenceNumber != null)
        title += ` #${this.invoice.sequenceNumber}`
      return title
    },
    activityStreamURL() {
      if(!this.$data.invoiceId)
        return null
      return `/accounts/invoices/${this.$data.invoiceId}/activitystream`
    },
    hasIntegrations() {
      return this.integrationList.filter(integration => integration.active).length > 0
    },
    integrationList() {
      if(this.$data.invoice.integrationData == null)
        return []
      return Object.values(this.$data.invoice.integrationData)        
    },
    isInvoiceWaitingPayment() {
      return this.invoice.status == InvoiceStatusTypes.invoiced
    },
    isInvoiceCompleted() {
      return this.invoice.status == InvoiceStatusTypes.paid
    },
    isInvoiceCancelled() {
      return this.invoice.status == InvoiceStatusTypes.cancelled
    },
    isInvoiceEditable() {
      if(this.invoice.status == InvoiceStatusTypes.invoiced && this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.accountAdministrator))
        return true
      return false
    },
    isInvoiceClosable() {
      if(this.invoice.status == InvoiceStatusTypes.invoiced && this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.accountAdministrator))
        return true
      return false
    },
    isInvoiceReSendable() {
      if(this.isInvoiceCancelled)
        return false
      return this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.invoice)
    },
    isAddPaymentAvailable() {
      if(!this.isInvoiceWaitingPayment)
        return false
      return this.userHasCreatePermission(this.PermissionTypes.AccountsPermissionTypes.payment)
    },
    isAddCreditNoteAvailable() {
      if(!this.isInvoiceWaitingPayment)
        return false
      return this.userHasCreatePermission(this.PermissionTypes.AccountsPermissionTypes.creditNote)
    }
  },
  data() {
    return {
      invoiceId: null,
      invoice: {},
      itemList: {
        itemData: [],
        form: {},
        overrideWarnings: false,
        editing: false,
        submitting: false
      },
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.InvoiceStatusTypes = InvoiceStatusTypes
    this.InvoiceStatusTypeLookup = InvoiceStatusTypeLookup
  },
  mounted() {
    this.$data.invoiceId = this.$route.params.id

    // Subscribe to all relevant endpoints
    this.subscribeEndpoint(`/accounts/invoices/${this.$data.invoiceId}`, () => {
      this.requestData()
    })

    // Load the invoice
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()

    this.unsubscribeEndpoint(`/accounts/invoices/${this.$data.invoiceId}`)
    this.$data.invoiceId = this.$route.params.id

    this.subscribeEndpoint(`/accounts/invoices/${this.$data.invoiceId}`, () => {
      this.requestData()
    })

    this.pageRefreshed()
    this.requestData()
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/accounts/invoices/${this.$data.invoiceId}`)
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.accountsService.getInvoice(this.$data.invoiceId),
      ]))
      .then(results => {
        this.invoice = results[0].data

        // Copy the invoice item list & modify for display/editing
        this.itemList.itemData = this.buildOrderItemList(this.invoice.invoiceItems, null, [], 0, 'parentInvoiceItemId')
      })
      .catch(err => this.handlePageLoadError(err))
    },
    displayInvoiceReport(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/accounts/invoices/${this.$data.invoiceId}/report?${params}`)
    },
    editInvoice() {
      this.displayModal(EditInvoiceDialog, { 
        invoiceId: parseInt(this.$data.invoiceId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Invoice Updated')
          this.reloadData()
        }
      })
    },
    sendInvoice() {
      this.displayModal(SendInvoiceDialog, { 
        invoiceId: parseInt(this.$data.invoiceId),
        contactId: parseInt(this.invoice.contactId),
        customContactEmail: this.invoice.customContactEmail,
      })
      .then((success) => {
        if(success) {
          this.successToast('Invoice Sent')
          this.reloadData()
        }
      })
    },
    addPayment() {
      this.displayModal(AddPaymentDialog, {}, {
        invoiceId: parseInt(this.$data.invoiceId),
        contactId: parseInt(this.invoice.contactId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Payment Received')
          this.reloadData()
        }
      })
    },
    addCreditNote() {
      this.displayModal(AddCreditNoteDialog, {}, {
        invoiceId: parseInt(this.$data.invoiceId),
        contactId: parseInt(this.invoice.contactId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Credit Note Created')
          this.reloadData()
        }
      })
    },
    closeInvoice() {
      this.displayModal(CloseInvoiceDialog, {
        invoiceId: parseInt(this.$data.invoiceId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Invoice Updated')
          this.reloadData()
        }
      })
    }
  }
}
</script>
